import React from 'react'
import type { RouteProps as ReactRouterRouteProps } from 'react-router-dom'
import { Route as ReactRouterRoute, Redirect } from 'react-router-dom'
import { useAuth } from 'react-auth0'
import { useUser } from '@paintscout/ui-business'
import type { Permission } from 'paintscout'
import type { FeatureProps } from './Feature/Feature'
import Feature from './Feature/Feature'
import Role from './Role/Role'
import ErrorBoundary from './ErrorBoundary/ErrorBoundary'
import NotAccessible from './NotAccessible'

export interface RouteProps extends ReactRouterRouteProps {
  auth?: boolean
  features?: FeatureProps['required']
  permission?: Permission
  roles?: string[]
  blockMasquerade?: boolean
  notAccessible?: boolean
}

/**
 * An enhanced Route component from react-router-dom that allows restrictions on the route
 *
 * note: `auth` must be true for `features`/`permission` props to take effect
 */
export default function Route({
  auth,
  features,
  permission,
  roles,
  blockMasquerade,
  notAccessible,
  ...routeProps
}: RouteProps) {
  const { session } = useAuth()
  const { isMasquerading } = useUser()

  if (auth && !session) {
    return null
  }

  if (blockMasquerade && isMasquerading) {
    return <Redirect to="/dashboard" />
  }

  return (
    <ErrorBoundary>
      <FeatureRoute features={features}>
        <RoleRoute permission={permission} roles={roles} notAccessible={notAccessible}>
          <ReactRouterRoute {...routeProps} />
        </RoleRoute>
      </FeatureRoute>
    </ErrorBoundary>
  )
}

function FeatureRoute({ features, children }: { features?: FeatureProps['required']; children: any }) {
  if (features) {
    return <Feature required={features}>{children}</Feature>
  }

  return children
}

function RoleRoute({
  children,
  permission,
  roles,
  notAccessible
}: {
  children: any
  permission?: Permission
  roles?: string[]
  notAccessible?: boolean
}) {
  const { profile, hasPermissions } = useUser()

  if (permission || roles) {
    const hasPermissionAccess = hasPermissions([permission]) || !permission
    const hasRoleAccess = roles?.length ? profile?.roles.some((role) => roles.includes(role)) : true
    const hasAccess = hasPermissionAccess && hasRoleAccess

    if (!hasAccess && notAccessible) {
      return <NotAccessible />
    }

    return (
      <Role permission={permission} roles={roles}>
        {children}
      </Role>
    )
  }

  return children
}
